import React, {
  createContext,
  forwardRef,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react';
import axios from 'axios';
import { readStoredWorkspaceIdentity } from 'src/workspaces/AdminWorkspaceProvider';
import { clientDocumentCompany } from './clientDocumentBranding';
import './documentTheme.css';

export type DocumentTheme = {
  schemaVersion: number;
  header: string;
  headerText: string;
  body: string;
  bodyText: string;
  stripe: string;
  grid: string;
  subtotal: string;
  subtotalText: string;
  total: string;
  totalText: string;
  warning: string;
  warningText: string;
  line: number;
  density: string;
  striped: boolean;
  documentHeader: {
    showLogo: boolean;
    logoData: string;
    logoSize: string;
    layout: string;
    showCompany: boolean;
    showEvent: boolean;
    divider: boolean;
    background: string;
    text: string;
    accent: string;
  };
};
const colors = [
  'header',
  'headerText',
  'body',
  'bodyText',
  'stripe',
  'grid',
  'subtotal',
  'subtotalText',
  'total',
  'totalText',
  'warning',
  'warningText'
];
export const documentThemeConfiguration = (
  selection: any
): DocumentTheme | null => {
  const theme = selection?.configuration;
  if (
    theme?.schemaVersion !== 1 ||
    !colors.every((key) => /^#[0-9a-f]{6}$/i.test(theme[key])) ||
    ![1, 2, 3].includes(theme.line) ||
    !theme.documentHeader ||
    !['background', 'text', 'accent'].every((key) =>
      /^#[0-9a-f]{6}$/i.test(theme.documentHeader[key])
    )
  )
    return null;
  return theme;
};
export const DocumentThemeContext = createContext<DocumentTheme | null>(null);
export const useDocumentTheme = () => useContext(DocumentThemeContext);
export function documentThemeStyle(
  theme: DocumentTheme | null
): React.CSSProperties {
  if (!theme) return {};
  const values: Record<string, string | number> = {};
  colors.forEach((key) => {
    values['--document-' + key] = theme[key];
  });
  values['--document-line'] = theme.line + 'px';
  values['--document-padding'] =
    theme.density === 'compact' ? '5px 6px' : '9px 8px';
  values['--document-alternate'] = theme.striped ? theme.stripe : theme.body;
  ['background', 'text', 'accent'].forEach((key) => {
    values['--document-brand-' + key] = theme.documentHeader[key];
  });
  return values as React.CSSProperties;
}

// Legacy document families render their totals independently. Classify existing rows,
// never reconstruct values or change the invoice/quote calculation state.
export function markDocumentThemeRows(root: HTMLElement) {
  root.querySelectorAll<HTMLTableRowElement>('tr').forEach((row) => {
    if (row.closest('[data-document-theme-exclude]')) return;
    const cells = Array.from(row.cells);
    const label =
      row.querySelector('[data-client-summary-label]')?.textContent?.trim() ||
      (cells.map((cell) => cell.textContent?.trim() ?? '').find(Boolean) ?? '');
    const subtotal = /^(?:(?:crew|staffing) sub\s?total|sub\s?total)\b/i.test(label);
    const total = !subtotal && (
      /^(?:grand total|total due|balance due|amount due|total amount|final total|admin fee|administrative fee|service fee|discount|deposit|paid deposit|paid amount|already paid|total paid|payments received|remaining|instant pay(?:ment)? fee|processing fee|payment method|tax|sales tax|surcharge|additional charges?|other charges?)\b/i.test(
        label
      ) || row.matches('[data-quote-summary-row], .client-pdf-summary-row') || Boolean(row.closest('.client-pdf-summary'))
    );
    if (total || subtotal)
      row.dataset.documentTotal = total ? 'grand' : 'subtotal';
    else delete row.dataset.documentTotal;
  });
}

type Props = React.HTMLAttributes<HTMLDivElement> & {
  document?: any;
  company?: any;
  refreshSelection?: boolean;
  deferThemeContent?: boolean;
  expectedCompanyId?: number;
  companyLoadError?: boolean;
  onRetryCompany?: () => void;
};
export const DocumentThemeScope = forwardRef<HTMLDivElement, Props>(
  function DocumentThemeScope(
    {
      document,
      company: explicitCompany,
      refreshSelection = false,
      deferThemeContent = false,
      expectedCompanyId,
      companyLoadError = false,
      onRetryCompany,
      children,
      style,
      ...props
    },
    forwardedRef
  ) {
    const documentCompany = explicitCompany ?? clientDocumentCompany(document);
    const company =
      expectedCompanyId && Number(documentCompany?.id) !== expectedCompanyId
        ? null
        : documentCompany;
    const [fresh, setFresh] = useState<{
      companyId: number;
      selected: any;
      error?: boolean;
    } | null>(null);
    const [retryRequest, setRetryRequest] = useState(0);
    const inherited = useDocumentTheme();
    const root = useRef<HTMLDivElement>(null);
    const realmSelection =
      deferThemeContent &&
      refreshSelection &&
      readStoredWorkspaceIdentity().kind === 'realm';
    useEffect(() => {
      if (!refreshSelection || !company?.id) return;
      const identity = readStoredWorkspaceIdentity();
      if (identity.kind !== 'realm') return;
      let current = true;
      const prefix =
        identity.principal.role === 'SUPER_ADMIN' ? '/platform' : '';
      axios
        .get(`${prefix}/companies/${company.id}/profile/table-themes/selection`)
        .then((response) => {
          if (current)
            setFresh({
              companyId: company.id,
              selected: response.data.data.selected
            });
        })
        .catch(() => {
          if (current)
            setFresh({ companyId: company.id, selected: null, error: true });
        });
      return () => {
        current = false;
      };
    }, [company?.id, refreshSelection, retryRequest]);
    const selectionPending =
      deferThemeContent &&
      (!company || (realmSelection && fresh?.companyId !== company.id));
    const selectionError =
      deferThemeContent &&
      Boolean(fresh && fresh.companyId === company?.id && fresh.error);
    const selected =
      fresh && fresh.companyId === company?.id && !fresh.error
        ? fresh.selected
        : company?.documentTheme;
    const theme = company ? documentThemeConfiguration(selected) : inherited;
    useEffect(() => {
      if (!root.current || !theme) return;
      const node = root.current;
      const update = () => markDocumentThemeRows(node);
      update();
      const observer = new MutationObserver(update);
      observer.observe(node, {
        childList: true,
        subtree: true,
        characterData: true
      });
      return () => observer.disconnect();
    }, [theme]);
    return (
      <DocumentThemeContext.Provider value={theme}>
        <div
          {...props}
          ref={(node) => {
            (root as React.MutableRefObject<HTMLDivElement | null>).current =
              node;
            if (typeof forwardedRef === 'function') forwardedRef(node);
            else if (forwardedRef) forwardedRef.current = node;
          }}
          data-document-theme={theme ? 'selected' : undefined}
          data-document-theme-pending={selectionPending ? 'true' : undefined}
          data-document-layout={theme ? 'split' : undefined}
          data-document-company={
            theme ? String(theme.documentHeader.showCompany) : undefined
          }
          data-document-event={
            theme ? String(theme.documentHeader.showEvent) : undefined
          }
          data-document-divider={
            theme ? String(theme.documentHeader.divider) : undefined
          }
          style={{ ...documentThemeStyle(theme), ...style }}
        >
          {selectionPending && !companyLoadError && (
            <div className="document-theme-loading" role="status">
              Loading company document appearance…
            </div>
          )}
          {deferThemeContent && companyLoadError && !company && (
            <div className="document-theme-load-error" role="alert">
              Company profile could not be loaded. Retry to display the quote banner and table.
              {onRetryCompany && (
                <button type="button" onClick={onRetryCompany}>
                  Retry company profile
                </button>
              )}
            </div>
          )}
          {selectionError && (
            <div className="document-theme-load-error" role="alert">
              Current document appearance could not be checked. Showing the available company appearance.
              <button
                type="button"
                onClick={() => {
                  setFresh(null);
                  setRetryRequest((value) => value + 1);
                }}
              >Retry theme</button>
            </div>
          )}
          {children}
        </div>
      </DocumentThemeContext.Provider>
    );
  }
);
